import * as AgmNames from "./const/agm-names";
import { default as CallbackRegistryFactory, CallbackRegistry, UnsubscribeFunction } from "callback-registry";
import { Glue42 } from "../../glue";
import { AppProps, Configuration, StartAppResult } from "./types";
import AppManagerImpl from "./app-manager";
import { Glue42Core } from "@glue42/core";
import { objectClone, validate } from "./helper";
import { isUndefinedOrNull } from "../activity/helpers/util";

export default class ApplicationImpl implements Glue42.AppManager.Application {

    private _registry: CallbackRegistry = CallbackRegistryFactory();

    // Additional properties of the Application
    private _props: AppProps;

    constructor(private _appManager: AppManagerImpl, private _name: string, private _agm: Glue42Core.AGM.API, private _configuration: () => Configuration) {

        // TODO: Replace string events with constants. The string declaration of the registry event name should only ever appear once. Ever. Period.

        // subscribe for events from app-manager
        _appManager.onInstanceStarted((instance) => {
            if (instance.application && instance.application.name !== this._name) {
                return;
            }

            this._registry.execute("instanceStarted", instance);
        });

        _appManager.onInstanceStopped((instance) => {
            if (instance.application && instance.application.name !== this._name) {
                return;
            }

            this._registry.execute("instanceStopped", instance);
        });

        _appManager.onAppRemoved((app) => {
            if (app.name !== this._name) {
                return;
            }

            this._registry.execute("appRemoved", app);
        });

        _appManager.onAppChanged((app) => {
            if (app.name !== this._name) {
                return;
            }

            this._registry.execute("appChanged", app);
        });

        _appManager.onAppAvailable((app) => {
            if (app.name !== this._name) {
                return;
            }
            this._props.IsReady = true;
            this._registry.execute("appAvailable", app);
        });

        _appManager.onAppUnavailable((app) => {
            if (app.name !== this._name) {
                return;
            }
            this._props.IsReady = false;
            this._registry.execute("appUnavailable", app);
        });
    }

    get name(): string { return this._name; }
    get title(): string { return this._props.Title; }
    get version(): string { return this._props.Version; }
    get autoStart(): boolean { return this._props.AutoStart; }
    get isShell(): boolean { return this._props.IsShell; }
    get caption(): string { return this._props.Caption; }
    get hidden(): boolean { return this._props.IsHidden; }
    get container(): string { return this._props.ApplicationName; }
    get activityType(): string { return this._props.ActivityType; }
    get activityWindowType(): string { return this._props.ActivityWindowType; }
    get windowSettings(): { [key: string]: any } {
        if (!this._props.Arguments) {
            return {};
        }
        return objectClone(this._props.Arguments);
    }
    get allowMultiple(): boolean { return this._props.AllowMultiple; }
    get available(): boolean { return this._props.IsReady || true; }
    get icon(): string { return this._props.Icon; }
    get iconURL(): string { return this._props.IconUrl; }
    get sortOrder(): number { return this._props.SortOrder; }

    get userProperties(): { [key: string]: any } {
        if (!this._props.UserProperties) {
            return {};
        }
        return objectClone(this._props.UserProperties);
    }

    get keywords(): string[] {
        if (!this._props.Keywords) {
            return [];
        }
        return this._props.Keywords;
    }

    get isActivity(): boolean {
        return this._props.ActivityType !== undefined && this._props.ActivityType !== "";
    }

    get configuration(): Glue42.AppManager.LegacyAppConfigProperties {
        return {
            autoStart: this._props.AutoStart,
            caption: this._props.Caption,
            hidden: this._props.IsHidden,
            container: this._props.ApplicationName,
            activityType: this._props.ActivityType,
            allowMultiple: this._props.AllowMultiple
        };
    }

    get instances() {
        return this._appManager.instances().filter((instance: Glue42.AppManager.Instance) => instance.application.name === this._name);
    }

    get type() {
        return this._props.Type;
    }

    get mode() {
        if (!this._props) {
            // no info about the app
            return "unknown";
        }

        // GD3 - we always have Mode from the stream object
        if (this._props.Mode && typeof this._props.Mode === "string") {
            return this._props.Mode.toLowerCase() as Glue42.Windows.WindowMode;
        }

        // GD2
        // return unknown for activity applications
        if (this.isActivity) {
            return "unknown";
        }

        // check definition -> hc.args.mode
        if (this._props.Arguments && this._props.Arguments.mode && typeof this._props.Arguments.mode === "string") {
            return this._props.Arguments.mode.toLowerCase() as Glue42.Windows.WindowMode;
        }

        // if no hc.args.mode, check hc.WindowStyleAttributes
        let styleAttributes = this._props.WindowStyleAttributes;
        if (styleAttributes) {
            // WindowStyleAttributes is not a valid JSON, we will need to find mode in the string
            styleAttributes = styleAttributes.split(" ").join("");
            const searchFor = "mode:\"";
            const modeIndex = styleAttributes.indexOf(searchFor);
            if (modeIndex !== -1) {
                const startModeIndex = modeIndex + searchFor.length;
                const stopModeIndex = styleAttributes.indexOf("\"", startModeIndex);
                const style = styleAttributes.substr(startModeIndex, stopModeIndex - startModeIndex);
                if (style && typeof style === "string") {
                    return style.toLowerCase() as Glue42.Windows.WindowMode;
                }
            }
        }

        // default mode for GD2
        return "flat";
    }

    public async getConfiguration() {
        const result = await this._agm.invoke(AgmNames.GetApplicationsMethodName, { v2: { apps: [this._name] } });
        const config = result.returned.applications[0];
        return config;
    }

    public updateFromProps(props: AppProps) {
        if (!this._props) {
            this._props = { Name: props.Name };
        }

        Object.keys(props).forEach((key) => {
            (this._props as any)[key] = (props as any)[key];
        });
    }

    public start(context?: unknown, options?: Glue42.AppManager.ApplicationStartOptions): Promise<Glue42.AppManager.Instance> {
        return new Promise<Glue42.AppManager.Instance>(async (resolve, reject) => {
            if (isUndefinedOrNull(context)) {
                context = {};
            } else if (this._configuration()?.throwErrors && typeof context !== "object" || Array.isArray(context)) {
                return reject(new Error(`Invalid "context" parameter - must be an object.`));
            }

            if (isUndefinedOrNull(options)) {
                options = {};
            } else if (this._configuration()?.throwErrors && typeof options !== "object") {
                return reject(new Error(`Invalid "options" parameter - must be an object.`));
            }

            const name = this._name;
            let waitForAGMInstance = options.waitForAGMReady ?? true;
            let startTimeout = 60000;
            if (typeof options.timeout === "number") {
                startTimeout = options.timeout * 1000;
            }

            const waitForApplicationInstance = (id: string) => {
                let unsub: () => void;
                // reject after some timeout
                const timeout = setTimeout(() => {
                    if (unsub) {
                        unsub();
                    }
                    reject(`timed out while waiting for instance id ${id} for app ${this.name}`);
                }, startTimeout);

                const waitFunc = (i: Glue42.AppManager.Instance) => {
                    if (i.id !== id) {
                        return;
                    }
                    if (unsub) {
                        unsub();
                        unsub = undefined;
                    }
                    clearTimeout(timeout);
                    resolve(i);
                };

                if (waitForAGMInstance) {
                    unsub = this._appManager.onInstanceAgmServerReady(waitFunc);
                } else {
                    // if not there subscribe for instance events
                    unsub = this._appManager.onInstanceStarted(waitFunc);
                }
            };

            try {
                const result = await this._agm.invoke<StartAppResult>(AgmNames.StartApplicationMethodName,
                    {
                        Name: name,
                        Context: context,
                        Options: options
                    },
                    "best",
                    {
                        methodResponseTimeoutMs: startTimeout
                    });

                const acsResult: StartAppResult = result.returned;
                if (typeof acsResult.timeout !== "undefined" && typeof options.timeout === "undefined") {
                    startTimeout = acsResult.timeout * 1000;
                }
                if (typeof acsResult.waitForInterop !== "undefined" && typeof options.waitForAGMReady === "undefined") {
                    waitForAGMInstance = acsResult.waitForInterop;
                }

                if (acsResult && acsResult.Id) {
                    if (this._appManager.mode === "startOnly") {
                        // we're in startOnly mode, no stream, so we will simulate the instance
                        // based on the data received from GD
                        const instance = this._appManager.handleInstanceStarted({
                            ActivityId: undefined,
                            IsActivityOwner: undefined,
                            Context: undefined,
                            Title: undefined,
                            AgmServers: undefined,
                            Id: acsResult.Id,
                            Name: acsResult.Name,
                        });
                        resolve(instance);
                    } else {
                        // we received instance from ACS, let's wait until it appears on the stream
                        waitForApplicationInstance(acsResult.Id);
                    }
                } else {
                    // we haven't received instance from ACS - this might happen if the app does not
                    // support tracking instances (e.g. we start internet explorer with some url)
                    resolve(undefined);
                }
            } catch (error) {
                reject(error.message ?? error);
            }
        });
    }

    public onInstanceStarted(callback: (instance: Glue42.AppManager.Instance) => any): UnsubscribeFunction {
        validate(callback, this._configuration());
        return this._registry.add("instanceStarted", callback);
    }

    public onInstanceStopped(callback: (instance: Glue42.AppManager.Instance) => any): UnsubscribeFunction {
        validate(callback, this._configuration());
        return this._registry.add("instanceStopped", callback);
    }

    public onAvailable(callback: (app: Glue42.AppManager.Application) => any): UnsubscribeFunction {
        validate(callback, this._configuration());
        if (this._props.IsReady) {
            setTimeout(() => {
                this._registry.execute("appAvailable", this);
            }, 0);
        }
        return this._registry.add("appAvailable", callback);
    }

    public onUnavailable(callback: (app: Glue42.AppManager.Application) => any): UnsubscribeFunction {
        validate(callback, this._configuration());
        if (this._props.IsReady === false) {
            setTimeout(() => {
                this._registry.execute("appUnavailable", this);
            }, 0);
        }
        return this._registry.add("appUnavailable", callback);
    }

    public onChanged(callback: (app: Glue42.AppManager.Application) => any): void {
        validate(callback, this._configuration());
        this._registry.add("appChanged", callback);
    }

    public onRemoved(callback: (app: Glue42.AppManager.Application) => any): void {
        validate(callback, this._configuration());
        this._registry.add("appRemoved", callback);
    }
}
